/**
 * BM-H4 — Guides Library (read-only)
 *
 * Lists the staff guides that back Bonsai Mate (residential, non-residential,
 * admin, accounts, plus the master index) as a browsable library. Each row
 * links to the reader at /guides/:key.
 *
 * Chrome mirrors the Policy Library (Brief 26a) — same list + reader shape.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, AlertCircle, ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { guideLook } from "./guideLook";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function GuidesLibrary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API}/api/hub/guides`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="guides-library-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading guides library...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="guides-library-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load guides library: {error}
      </div>
    );
  }
  if (!data || !data.guides || data.guides.length === 0) {
    return (
      <div data-testid="guides-library-empty" className="p-6 text-gray-600">
        <BookOpen className="inline-block mr-2" size={16} />
        No guides are available.
      </div>
    );
  }

  return (
    <div data-testid="guides-library-page" className="p-4 sm:p-6 space-y-6 max-w-6xl">
      <div className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 p-5 shadow-xl shadow-indigo-950/40">
        <div aria-hidden className="agents-orb absolute -top-24 -right-16 h-72 w-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(14,165,233,0.28), transparent 65%)" }} />
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ring-white/20 shadow-lg shadow-indigo-900/40"
              style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6 55%, #0ea5e9)" }}>
              <BookOpen className="h-6 w-6 text-white" aria-hidden="true" />
              <Sparkles size={14} className="agents-twinkle absolute -top-1 -right-1 text-amber-200" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-bold agents-title leading-tight">Guides Library</h1>
              <p className="text-sm text-indigo-100/75">How we do things here, by role. Mate answers from these too.</p>
            </div>
          </div>
          <span className="cm-glass rounded-full px-3 py-1 text-sm text-white ring-1 ring-white/15">{data.guides.length} guides</span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.guides.map((g) => {
          const { Icon, style } = guideLook(g.key);
          return (
            <Link
              key={g.key}
              to={`/guides/${g.key}`}
              style={style}
              data-testid={`guides-library-row-${g.key}`}
              className="cm-card group relative flex flex-col gap-2 overflow-hidden rounded-xl border border-white/[0.07] bg-navy-card p-4"
            >
              <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg, var(--c1), var(--c2))" }} />
              <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-25"
                style={{ background: "radial-gradient(circle, var(--c1), transparent 70%)" }} />
              <div className="relative flex items-center gap-2.5">
                <span className="cm-tile flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-slate-100" data-testid={`guides-library-link-${g.key}`}>{g.title}</span>
                <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true" />
              </div>
              <p className="relative text-sm text-slate-400">{g.description}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
